import { Box, Button, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useNavigate } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import avatar from '@/assets/images/avatar.png';
import { useFileUploader } from '@/hooks/useFileUploader';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import { FileInfo } from '@/api/DTO/common.interface';
import {
	UpdateUserRequest,
	UserDetailDTO,
	UserRequest,
} from '@/api/DTO/user/userList.interface';
import { useUpdateUserInfoMutation } from '@/api/user/userApiSlice';

interface StallInfoFormProps {
	type: FormType;
	setType: Dispatch<SetStateAction<FormType>>;
	userDetail: UserDetailDTO;
}

export default function EditUserInfoForm({
	type,
	userDetail,
	setType,
}: StallInfoFormProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const { uploadFile } = useFileUploader();
	const [avatarFileObj, setAvatarFileObj] = useState<FileInfo | null>(
		userDetail.avatar || null
	);
	console.log(avatarFileObj);
	const [updateUserInfo] = useUpdateUserInfoMutation();

	const ageList = [
		{ id: 0, name: '<25' },
		{ id: 1, name: '25-34' },
		{ id: 2, name: '35-44' },
		{ id: 3, name: '45-54' },
		{ id: 4, name: '>54' },
	];
	const sexList = [
		{ id: 0, name: t('user.boy') },
		{ id: 1, name: t('user.girl') },
		{ id: 2, name: t('user.guarded') },
	];

	const defaultValues: DefaultValues<UserRequest> = userDetail;

	const formSchema = Yup.object().shape({
		nickname: Yup.string()
			.required(t('errorMessage.required'))
			.min(1, t('errorMessage.min3'))
			.max(10, t('errorMessage.max100')),

		gender: Yup.string().required(t('errorMessage.pleaseSelect')),
		ageGroup: Yup.string().required(t('errorMessage.pleaseSelect')),
		hasMeditationHabit: Yup.string()
			.required(t('errorMessage.pleaseSelect'))
			.oneOf(['true', 'false'], t('errorMessage.pleaseSelect')),
		receivePromotion: Yup.string()
			.required(t('errorMessage.pleaseSelect'))
			.oneOf(['true', 'false'], t('errorMessage.pleaseSelect')),
	});
	const methods = useForm<UserDetailDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const watchMonthOfBirth = methods.watch('monthOfBirth');

	const getDay = () => {
		if (!watchMonthOfBirth) return [];
		if ([1, 3, 5, 7, 8, 10, 12].includes(watchMonthOfBirth)) {
			return [...Array(31).keys()].map((i) => ({
				id: i + 1,
				name: `${i + 1}`,
			}));
		}

		if ([4, 6, 9, 11].includes(watchMonthOfBirth)) {
			return [...Array(30).keys()].map((i) => ({
				id: i + 1,
				name: `${i + 1}`,
			}));
		}

		if ([2].includes(watchMonthOfBirth)) {
			return [...Array(29).keys()].map((i) => ({
				id: i + 1,
				name: `${i + 1}`,
			}));
		}
	};

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<UpdateUserRequest> = async (data) => {
		const obj: UpdateUserRequest = {
			userId: data.id || '',
			ageGroup: data.ageGroup,
			dayOfBirth: data.dayOfBirth,
			gender: data.gender,
			hasMeditationHabit: data.hasMeditationHabit,
			monthOfBirth: data.monthOfBirth,
			nickname: data.nickname,
			receivePromotion: data.receivePromotion,
			avatar: avatarFileObj || null,
		};
		const response = await updateUserInfo(obj);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateUserSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			setType(FormType.view);
		}
	};

	const handleDeleteImage = () => {
		setAvatarFileObj(null);
	};

	const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		const res = await uploadFile(selectedFile);
		if (res) {
			setAvatarFileObj(res);
			// methods.setValue('audioFileKey', res.name);
			// methods.trigger('audioFileKey');
			// setAudioFileKey(res);
		}
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<Box display='flex' justifyContent='space-between'>
						<Typography variant='h6'>{t('user.userData')}</Typography>
						<Box>
							<Button
								onClick={() => {
									setType(FormType.view);
								}}
							>
								{t('button.cancel')}
							</Button>
							<Button
								type='submit'
								variant='contained'
								disabled={!needConfirm && type === FormType.create}
							>
								{t('button.save')}
							</Button>
						</Box>
					</Box>

					<Grid container spacing={3} marginTop={0} marginBottom={3}>
						<Grid item xs={6}>
							<AppTextField
								control={methods.control}
								name='username'
								label={t('user.userName')}
								disabled
							/>
						</Grid>
						<Grid item xs={6}>
							<AppTextField
								control={methods.control}
								name='nickname'
								label={t('user.nickName')}
								maxLength={10}
							/>
						</Grid>
						<Grid item xs={6}>
							<AppDropDownMenu
								control={methods.control}
								name='gender'
								label={t('user.sex')}
								options={sexList}
								required
							/>
						</Grid>
						<Grid item xs={6}>
							<AppDropDownMenu
								control={methods.control}
								name='ageGroup'
								label={t('user.ageGroup')}
								options={ageList}
								required
							/>
						</Grid>
						<Grid item xs={3}>
							<Typography variant='caption'>{t('user.birthday')}</Typography>
							<AppDropDownMenu
								control={methods.control}
								name='monthOfBirth'
								label={t('user.month')}
								options={[...Array(12).keys()].map((i) => ({
									id: i + 1,
									name: `${i + 1}`,
								}))}
								required
								handleChange={() => {
									methods.setValue('dayOfBirth', null);
								}}
							/>
						</Grid>
						<Grid item xs={3}>
							<div>&nbsp;</div>
							<AppDropDownMenu
								control={methods.control}
								name='dayOfBirth'
								label={t('user.day')}
								options={getDay() || []}
								required
							/>
						</Grid>
						<Grid item xs={6}></Grid>
						<Grid item xs={6}>
							<Typography variant='body2' color='common.neutral40'>
								{t('user.isMeditation')}
							</Typography>
							<AppRadioGroup
								control={methods.control}
								name='hasMeditationHabit'
								row
								options={[
									{ id: true, name: t('common.yes') },
									{ id: false, name: t('common.no') },
								]}
							/>
						</Grid>
						<Grid item xs={6}>
							<Typography variant='body2' color='common.neutral40'>
								{t('user.isReceiveEmail')}
							</Typography>
							<AppRadioGroup
								control={methods.control}
								name='receivePromotion'
								row
								options={[
									{ id: true, name: t('common.yes') },
									{ id: false, name: t('common.no') },
								]}
							/>
						</Grid>
						<Grid item xs={6}>
							<Typography variant='body2' color='common.neutral40'>
								{t('user.customAvatar')}
							</Typography>
							<Box display={'flex'} alignItems={'center'}>
								<img
									src={avatarFileObj?.url ? avatarFileObj?.url : avatar}
									width={80}
									height={80}
									style={{ borderRadius: '50%' }}
								/>
								<Button
									component='label'
									sx={{ height: 24, marginLeft: '24px' }}
								>
									{t('common.uploadImg')}
									<VisuallyHiddenInput
										type='file'
										name='thumbnail'
										accept='image/*'
										onChange={handleUpload}
									/>
								</Button>
								{avatarFileObj?.url && (
									<Button
										component='label'
										sx={{ height: 24, marginLeft: '24px' }}
										color='error'
										onClick={handleDeleteImage}
									>
										{t('button.deleteImage')}
									</Button>
								)}
							</Box>
						</Grid>
					</Grid>
				</form>
			</FormProvider>
		</>
	);
}
